/**
 * v-backdrop-close directive — "Klick daneben schliesst", ohne dass eine
 * Textmarkierung im Modal das Modal schliesst.
 *
 * Usage (ersetzt `@click.self="close"` auf dem Backdrop-Element):
 *   <div class="modal fade show d-block" v-backdrop-close="closeEditor">
 *   <div class="crm-modal-overlay" v-backdrop-close="() => (detail = null)">
 *
 * Der Wert ist eine FUNKTION. Anders als bei `@click` wertet Vue den Ausdruck
 * einer Direktive beim Rendern aus — `v-backdrop-close="detail = null"` wuerde
 * also sofort zuschlagen. Zuweisungen und Aufrufe gehoeren deshalb in einen
 * Arrow-Wrapper, eine blosse Funktionsreferenz wird direkt uebergeben.
 *
 * Warum es die Direktive gibt:
 *   `@click.self` prueft `event.target === event.currentTarget`. Der Browser
 *   feuert `click` aber nicht dort, wo die Maustaste gedrueckt wurde, sondern
 *   auf dem gemeinsamen Vorfahren von mousedown- und mouseup-Ziel. Wer im
 *   Dialog Text markiert und die Taste ueber dem Backdrop loslaesst, trifft
 *   damit exakt den Backdrop — `.self` greift, das Modal schliesst mitten in
 *   der Markierung. Ueber das click-Event allein ist dieser Fall vom echten
 *   Klick daneben nicht zu unterscheiden.
 *
 *   Deshalb zaehlt hier der ANFANG des Klicks: nur ein mousedown auf dem
 *   Backdrop selbst schaltet das Schliessen scharf.
 *
 * Siehe [docs/learnings/2026-09-09-modal-schliesst-beim-textmarkieren.md].
 */

import type { Directive } from 'vue'

type BackdropHandler = (e: MouseEvent) => void

interface BackdropState {
  handler: BackdropHandler | undefined
  armed: boolean
  onMousedown: (e: MouseEvent) => void
  onClick: (e: MouseEvent) => void
}

const STATE = Symbol('v-backdrop-close')

type Hosted = HTMLElement & { [STATE]?: BackdropState }

export const vBackdropClose: Directive<Hosted, BackdropHandler> = {
  mounted(el, binding) {
    const state: BackdropState = {
      handler: binding.value,
      armed: false,

      onMousedown(e) {
        state.armed = e.target === el
      },

      onClick(e) {
        if (e.target !== el) return
        // detail === 0 heisst: kein echter Mausklick (Tastatur, el.click()).
        // Dazu gab es kein mousedown, das scharf schalten konnte — solche
        // Klicks sind nie eine Markierung und duerfen durch.
        if (!state.armed && e.detail !== 0) return
        state.armed = false
        state.handler?.(e)
      },
    }

    el[STATE] = state
    el.addEventListener('mousedown', state.onMousedown)
    el.addEventListener('click', state.onClick)
  },

  updated(el, binding) {
    const state = el[STATE]
    if (state) state.handler = binding.value
  },

  unmounted(el) {
    const state = el[STATE]
    if (!state) return
    el.removeEventListener('mousedown', state.onMousedown)
    el.removeEventListener('click', state.onClick)
    delete el[STATE]
  },
}
